import Uppy, { UploadResult, UppyFile, UppyOptions } from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3, { AwsS3UploadParameters, type AwsBody } from '@uppy/aws-s3';

import NgConfig from '../NgConfig';
import { UppyOptionsWithOptionalRestrictions } from '@uppy/core/lib/Uppy';

type UploaderOptions = {
    csrfToken: string
    allowedFileTypes: string
    singleFile: boolean
}

enum UploaderEvent {
    uploadComplete = 'uploadComplete',
    uploadFailed = 'uploadFailed'
}

type Meta = any;

class Uploader {
    csrfToken: string
    uppy: Uppy<Meta, AwsBody> | null
    options: UploaderOptions
    allowedTypes: string = "*/*"
    singleFile: boolean = false
    eventNames: string[] = ['uploadComplete', 'uploadFailed']

    constructor(options: UploaderOptions) {
        this.options = options
        this.csrfToken = options.csrfToken
        this.allowedTypes = options.allowedFileTypes
        this.uppy = null
        this.singleFile = options.singleFile

        const allowedFileTypes = this.allowedTypes.split(",")

        const s3args = {
            id: "NG_AWSPlugin",
            shouldUseMultipart: () => false,
            getUploadParameters: this.getUploadParameters.bind(this),
        }

        const uppyOptions: UppyOptionsWithOptionalRestrictions<any, AwsBody> = {
            autoProceed: true,
            allowMultipleUploadBatches: false,
            restrictions: {
                allowedFileTypes: allowedFileTypes,
                maxFileSize: NgConfig.MAX_UPLOAD_FILE_SIZE
            },
            onBeforeUpload: (files: { [key: string]: UppyFile<Meta, AwsBody> }) => {
                console.log("Before upload files: ", files)
                if (Object.keys(files).length > 1) {
                    alert("As a free user, you can only upload one file at a time.")
                    return false
                }
                return true
            }
        }
        if (this.singleFile) {
            uppyOptions.restrictions && (uppyOptions.restrictions.maxNumberOfFiles = 1)
        }
        this.uppy = new Uppy<Meta, AwsBody>(uppyOptions)
            // @ts-ignore
            .use(Dashboard, {
                id: "tool",
                inline: true,
                target: '#uppy-tool-dashboard',
                showProgressDetails: true,
                width: 384, height: 124,
                singleFileFullScreen: true,
                disableInformer: true
            })
            // @ts-ignore
            .use(AwsS3, s3args)
            .on('complete', async (result: any) => {
                console.log("Uppy done: ", result)
                const file_ids = result.successful.map((f: any) => f.meta.ng_id)
                const sizes = result.successful.map((f: any) => f.size)
                await this.reportUploadSuccess(file_ids, sizes)
            })
    }

    alertOnMultipleFiles(files: { [key: string]: UppyFile<Meta, AwsBody> }) {
        if (Object.keys(files).length > 1) {
            this.uppy?.info("As a free user, you can only upload one file at a time.")
            return false
        }
        return true
    }

    async getUploadParameters(file: UppyFile<Meta, {}>): Promise<AwsS3UploadParameters> {
        let res: AwsS3UploadParameters = { url: '', method: 'PUT', fields: {}, headers: {} }
        try {
            const response = await fetch(NgConfig.UPLOAD_URL, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                    'X-CSRFToken': this.csrfToken,
                    mode: 'same-origin'
                },
                body: JSON.stringify({
                    file_name: file.name,
                    file_type: file.extension,
                }),
            })
            if (!response.ok) {
                this._dispatchEvent(UploaderEvent.uploadFailed, { error: response.statusText })
            } else {
                const data = await response.json()
                file.meta.ng_id = parseInt(data.id as string)
                res = {
                    url: data.url,
                    method: 'PUT',
                    fields: {}, // data.fields,
                    headers: {
                        'Content-Type': file.extension || 'application/octet-stream',
                    },
                }
            }
        } catch (e: any) {
            this._dispatchEvent(UploaderEvent.uploadFailed, { error: e.message })
        }
        return res

    }

    _dispatchEvent(eventName: string, detail: any) {
        document.dispatchEvent(new CustomEvent(eventName, { detail }))
    }

    on(eventName: string, callback: (event: Event) => void) {
        document.addEventListener(eventName, callback)
    }

    off(eventName: string, callback: (event: Event) => void) {
        document.removeEventListener(eventName, callback)
    }

    off_all_events() {
        this.eventNames.forEach(eventName => {
            document.removeEventListener(eventName, () => { })
        })
    }

    async reportUploadSuccess(ids: number[], sizes: number[]) {
        try {
            const res = await fetch(NgConfig.FINISH_URL, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'content-type': 'application/json',
                    'X-CSRFToken': this.csrfToken,
                    mode: 'same-origin'
                },
                body: JSON.stringify({
                    //url: response.body.uploadURL, 
                    file_ids: ids,
                    sizes: sizes
                }),
            })
            if (!res.ok) this._dispatchEvent(UploaderEvent.uploadFailed, { error: res.statusText })
            else {
                //@ts-ignore
                const local_refname = refname
                eventRecorder.captureEvent('upload_complete');
                window.location.replace(`/tools/${local_refname}/params` + "?file_ids=" + ids.join(","))
            }
        } catch (e: any) {
            this._dispatchEvent(UploaderEvent.uploadFailed, { error: e.message })
        }
    }


}

// Inputs here are delivered via global vars
// @ts-ignore
const uploader = new Uploader({ csrfToken: csrf, allowedFileTypes: allowedFileTypes, singleFile: singleFile })
uploader.on(UploaderEvent.uploadFailed, (e: any) => {
    eventRecorder.captureEvent('upload_failed', { error: e.detail.error });
    alert("Upload failed: " + e.detail.error)
})